import { useState } from 'react';
import { Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { TablePageLayout } from '../components/TablePageLayout';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCostRefSearch } from '../api/costrefApi';
import { fetchAuthStatus } from '../api/authApi';

export function CostRefPage() {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: authData } = useQuery({
    queryKey: ['auth-status'],
    queryFn: fetchAuthStatus,
  });

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['costref-search', searchTerm],
    queryFn: fetchCostRefSearch,
    refetchOnWindowFocus: false,
    enabled: !!searchTerm,
    retry: (failureCount, error) => {
      if (error.status === 400) {
        return false;
      }
      return failureCount < 3;
    },
  });

  const executeSearch = () => {
    if (query) {
      queryClient.resetQueries({ queryKey: ['costref-search', query] });
      setSearchTerm(query);
    } else {
      setSearchTerm('');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      executeSearch();
    }
  };

  const errorMessage = isError ? (error.response?.error || error.response?.message || error.message) : '';

  const canSeeCostRef = authData?.user?.permissions?.includes('agent_portal.bo_tools');

  return (
    <TablePageLayout
      title="Costref search"
      description="Find invoice lines by cost reference."
      action={(
        <div className="d-flex gap-2">
          <Form.Control
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search cost reference"
            className="w-100"
            style={{ maxWidth: '18rem' }}
            disabled={isFetching}
          />
          <Button 
            variant="success" 
            onClick={executeSearch} 
            disabled={isFetching || !query}
          >
            {isFetching ? <Spinner animation="border" size="sm" /> : 'Search'}
          </Button>
        </div>
      )}
      isError={isError && !canSeeCostRef}
      error={!canSeeCostRef ? { message: 'You do not have permission to access this page.' } : error}
    >
      {!canSeeCostRef ? (
        <div className="p-3">
          <div className="alert alert-danger">
            You do not have permission to access this page.
          </div>
        </div>
      ) : (
        <>
          {errorMessage && (
            <div className="alert alert-danger mx-3 mb-3">
              {errorMessage}
            </div>
          )}
          <Table hover className="mb-0 align-middle sticky-header">
            <thead>
              <tr>
                <th>Company</th>
                <th>Cost ref</th>
                <th>Product</th>
                <th>Price</th>
                <th>Invoice date</th>
                <th>Invoice</th>
                <th>Invoice total</th>
              </tr>
            </thead>
            <tbody>
              {data?.invoice_lines.map((invoice_line) => (
                <tr key={invoice_line.mid}>
                  <td><a href={invoice_line.invoice.partner.url} target='_blank' rel="noreferrer">{invoice_line.invoice.partner.name}</a></td>
                  <td>{invoice_line.cost_ref}</td>
                  <td>{invoice_line.name || '-'}</td>
                  <td>{formatCurrency(invoice_line.price_total)}</td>
                  <td>{formatDate(invoice_line.invoice.invoice_date, 'P')}</td>
                  <td><a href={invoice_line.invoice.url} target='_blank' rel="noreferrer">{invoice_line.invoice.name}</a></td>
                  <td>{formatCurrency(invoice_line.invoice.amount_total)}</td>
                </tr>
              ))}
              {!isFetching && data?.invoice_lines.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-muted">
                    No results found for "{searchTerm}"
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </TablePageLayout>
  );
}
